import Edit from "@mui/icons-material/Edit";
import Restore from "@mui/icons-material/Restore";
import { Button, Divider, IconButton, Stack, Tooltip } from "@mui/material";
import { useAtomValue } from "jotai";
import { isEqual } from "lodash-es";
import { Link } from "react-router";
import { usePermissions } from "../../server/auth/use-permissions";
import { brandingAtom } from "../brand-kit/store";
import { BrandColorSelect } from "./brand-color-select";
import { BrandLogoSelect } from "./brand-logo-select";
import { ImageStyle } from "./image-style";

import type { BrandKitColor, BrandKitLogo } from "../brand-kit/server/brand-kit-service";
import type { BrandInfoSelected, BrandKitManager } from "../brand-kit/types";
import type { StackProps } from "@mui/material";
import type { FC } from "react";

interface CompactBrandingBarProps {
  manager: BrandKitManager;
  branding: unknown;
  defaultBranding?: BrandInfoSelected;
  brandKit?: {
    choices: {
      colors: BrandKitColor[];
      logos: BrandKitLogo[];
    };
  };
  visualStyleSelector?: boolean;
  imageStyleSelector?: boolean;
  hasBranding: boolean;
  useSelectedLogo?: boolean;
  onReset?: () => void;
}

export const CompactBrandingBar: FC<CompactBrandingBarProps & StackProps> = ({
  defaultBranding,
  brandKit,
  manager,
  hasBranding,
  visualStyleSelector = false,
  useSelectedLogo = true,
  onReset,
  imageStyleSelector = false,
  ...props
}) => {
  const selectedBranding = useAtomValue(brandingAtom);
  const { super_admin } = usePermissions();
  const showEdit = hasBranding && super_admin;
  const showReset = !isEqual(selectedBranding, defaultBranding);

  return hasBranding ? (
    <Stack
      direction="row"
      {...props}
      sx={[
        {
          gap: 1,
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      <ImageStyle
        visualStyleSelector={visualStyleSelector}
        imageTypeSelector={imageStyleSelector}
        manager={manager}
      />

      <Stack direction="column" sx={{ gap: 0.5 }}>
        {showEdit && (
          <Tooltip title="Edit Brand" placement="right" arrow>
            <Link to="/manage/brand/brand_kit">
              <IconButton
                data-analytics-id="branding-bar-edit"
                size="small"
                data-testid="branding-bar-edit-button"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Link>
          </Tooltip>
        )}

        {showReset && (
          <Tooltip title="Restore default" placement="right" arrow>
            <IconButton
              data-analytics-id="branding-bar-compact-reset"
              onClick={onReset}
              size="small"
              data-testid="branding-bar-reset-button"
            >
              <Restore fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {(showReset || showEdit) && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}
      <BrandColorSelect
        selected={selectedBranding.color}
        colors={brandKit?.choices.colors ?? []}
        manager={manager}
      />
      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
      <BrandLogoSelect
        selected={selectedBranding.logo}
        logos={brandKit?.choices.logos ?? []}
        manager={manager}
        defaultBranding={defaultBranding}
        useSelectedLogo={useSelectedLogo}
      />
    </Stack>
  ) : (
    <Link to="/manage/brand/brand_kit">
      <Button data-analytics-id="branding-bar-link-add" sx={{ my: 2 }}>
        Add Branding
      </Button>
    </Link>
  );
};
